import {
  type GetListParams,
  type QueryFunctionContext,
  HttpError,
} from "react-admin";

import { createApiClient } from "@/api";
import { HTTP_STATUS } from "@/constants/http";
import { type DataProviderSchema } from "@/utils";

import { type CampaignTdsFilter } from "./types";

const apiClient = createApiClient("jsonld");
const BASE_URL = "/api/vpn/campaign_tds";

const fetchListData = async ({
  filter,
}: GetListParams & QueryFunctionContext) => {
  const typedFilter = filter as CampaignTdsFilter | undefined;
  const response = await apiClient.GET(BASE_URL, {
    params: {
      query: {
        pagination: false,
        campaign: typedFilter?.campaign,
      },
    },
  });

  const data = response.data?.["hydra:member"] ?? [];
  const total = response.data?.["hydra:totalItems"] ?? 0;

  return {
    data,
    total,
  };
};

export const campaignTdsSchema: DataProviderSchema<"CampaignTds", "jsonld"> = {
  list: fetchListData,
  edit: async ({
    id,
    data: {
      campaign = "",
      landing = "",
      countrySet = [],
      modelSet = [],
      osSet = [],
    },
  }) => {
    const response = await apiClient.PUT(`${BASE_URL}/{id}`, {
      params: {
        path: { id: String(id) },
      },
      body: {
        campaign,
        landing,
        countrySet,
        modelSet,
        osSet,
      },
    });

    if (!response.data)
      throw new HttpError(
        "Something went wrong!",
        HTTP_STATUS.INTERNAL_SERVER_ERROR,
      );

    return {
      data: response.data,
    };
  },
  create: async ({
    data: {
      landing = "",
      campaign = "",
      countrySet = [],
      modelSet = [],
      osSet = [],
    },
  }) => {
    const { data } = await apiClient.POST(BASE_URL, {
      body: {
        landing,
        campaign,
        countrySet,
        modelSet,
        osSet,
      },
    });

    if (!data) throw new Error("Something went wrong!");

    return {
      data,
    };
  },
  delete: (async (entity: { id: string }) =>
    await apiClient.DELETE(`${BASE_URL}/{id}`, {
      params: {
        path: { id: entity.id },
      },
    })) as unknown as NonNullable<
    DataProviderSchema<"CampaignTds", "jsonld">["delete"]
  >,
};
